// src/components/achievements/SyncProgressButton.tsx (Fixed type issue)
import React, { useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { syncUserProgress } from '../../services/userProgressService';
import { useLanguage } from '../../contexts/LanguageContext';

type Props = {
  characterId?: string;
  className?: string;
  onSyncComplete?: () => void;
  compact?: boolean; // For a smaller button version
};

const SyncProgressButton: React.FC<Props> = ({ 
  characterId, 
  className = '', 
  onSyncComplete,
  compact = false
}) => {
  const { t } = useLanguage();
  const [syncing, setSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSync = async () => {
    if (syncing) return;
    
    try {
      setSyncing(true);
      setError(null);
      
      const result = await syncUserProgress(characterId);
      
      if (result) {
        setLastSynced(new Date());
        if (onSyncComplete) onSyncComplete();
      }
    } catch (err: any) {
      console.error('Error syncing progress:', err);
      setError(err.message || t('achievements.errorSyncingProgress'));
    } finally {
      setSyncing(false);
    }
  };

  if (compact) {
    return (
      <button
        onClick={handleSync}
        disabled={syncing}
        className={`inline-flex items-center p-1 rounded-md ${
          syncing ? 'text-gray-400 cursor-wait' : 'text-destiny-blue hover:text-destiny-gold'
        } ${className}`}
        title={syncing ? t('common.syncing') : t('achievements.syncProgress')}
      >
        <ArrowPathIcon className={`h-5 w-5 ${syncing ? 'animate-spin' : ''}`} />
      </button>
    );
  }

  return (
    <div className={className}>
      <button
        onClick={handleSync}
        disabled={syncing}
        className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${
          syncing 
            ? 'bg-gray-600 text-gray-300 cursor-wait' 
            : 'bg-destiny-blue text-white hover:bg-opacity-90'
        }`}
      >
        <ArrowPathIcon className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
        {syncing ? t('common.syncing') : t('achievements.syncProgress')}
      </button>
      
      {error && (
        <div className="text-destiny-red text-xs mt-1">
          {error}
        </div>
      )}
      
      {lastSynced && !error && (
        <div className="text-gray-400 text-xs mt-1">
          {/* Formatiere die Zeit direkt statt Platzhalter zu verwenden */}
          {t('achievements.lastSyncedAt')}: {lastSynced.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
};

export default SyncProgressButton;